import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import RatingStars from "@/components/RatingStars";
import ReviewsSection from "@/components/ReviewsSection";
import { ArrowLeft, MapPin, Phone, Globe, Trash2 } from "lucide-react";

export default function HotelDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [h, setH] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/hotels/${id}`);
      setH(data);
    } catch { setH(null); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const remove = async () => {
    if (!window.confirm(`¿Eliminar el hotel ${h.name}?`)) return;
    await api.delete(`/hotels/${h.id}`);
    window.location.href = "/hoteles";
  };

  if (loading) return <div className="max-w-3xl mx-auto px-6 pt-20">Cargando…</div>;
  if (!h) return <div className="max-w-3xl mx-auto px-6 pt-20" data-testid="hotel-not-found">Hotel no encontrado.</div>;

  const canDelete = user && ["admin", "moderator"].includes(user.role);

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 pt-10 pb-24" data-testid="hotel-detail-page">
      <Link to="/hoteles" className="inline-flex items-center gap-2 text-xs mb-6" style={{ color: "var(--text-secondary)" }} data-testid="hotel-back">
        <ArrowLeft size={12} /> Volver a hoteles
      </Link>

      {h.photo_url && (
        <div className="mb-6 overflow-hidden" style={{ aspectRatio: "16/7", background: "var(--bg-surface)" }}>
          <img src={h.photo_url} alt={h.name} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Hotel</div>
          <h1 className="font-serif text-4xl md:text-5xl font-light" style={{ color: "var(--text-primary)" }} data-testid="hotel-name">
            {h.name}
          </h1>
          <p className="mt-3 inline-flex items-center gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
            <MapPin size={12} /> {h.city}{h.state && `, ${h.state}`}
            {h.address && <span className="ml-2">· {h.address}</span>}
          </p>
          <div className="flex items-center gap-3 mt-3">
            <RatingStars value={h.rating_avg || 0} size={18} testid="hotel-rating" />
            <span className="text-sm" style={{ color: "var(--vip-gold)" }}>
              {h.rating_avg > 0 ? `${h.rating_avg.toFixed(1)} (${h.rating_count} reseñas)` : "Sin reseñas"}
            </span>
          </div>
        </div>

        {canDelete && (
          <button
            onClick={remove}
            className="btn-secondary text-xs inline-flex items-center gap-2"
            style={{ color: "#ef6d6d", borderColor: "#ef6d6d" }}
            data-testid="hotel-delete"
          >
            <Trash2 size={12} /> Eliminar
          </button>
        )}
      </div>

      {(h.phone || h.website) && (
        <div className="flex gap-3 flex-wrap mb-6">
          {h.phone && (
            <a href={`tel:${h.phone}`} className="btn-ghost text-xs inline-flex items-center gap-2" data-testid="hotel-phone">
              <Phone size={12} /> {h.phone}
            </a>
          )}
          {h.website && (
            <a href={h.website} target="_blank" rel="noreferrer" className="btn-ghost text-xs inline-flex items-center gap-2" data-testid="hotel-website">
              <Globe size={12} /> Sitio web
            </a>
          )}
        </div>
      )}

      {h.notes && (
        <div className="mb-8 p-5" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}>
          <div className="overline mb-2">Notas</div>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{h.notes}</p>
        </div>
      )}

      <ReviewsSection
        entity="hotel"
        entityId={h.id}
        ratingAvg={h.rating_avg || 0}
        ratingCount={h.rating_count || 0}
        onChange={load}
      />

      <p className="text-xs mt-5" style={{ color: "var(--text-muted)" }}>
        Agregado por {h.created_by_name} · {new Date(h.created_at).toLocaleDateString("es-MX")}
      </p>
    </div>
  );
}
